import { FriendlyError } from '@cibo/ui'
import LoadingButton from '@mui/lab/LoadingButton'
import { Button, CircularProgress, Stack } from '@mui/material'
import { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useFormToken } from '../../queries'
import {
  authDirectLoginWithPassword,
  authDirectLoginWithVerificationCode,
  authDirectRequestVerificationCode,
  selectAuthDirectLoginError,
  selectAuthDirectLoginLoading,
  selectAuthUserId,
} from '../../store'
import EmulateUserForm from '../EmulateUserForm'
import { ForgotPassword } from '../ForgotPassword'
import DirectLoginCodeForm from './DirectLoginCodeForm'
import DirectLoginPasswordForm from './DirectLoginPasswordForm'

type OwnProps = {
  code?: string
  onLogout?(): void
  onValidationStep(a?: any): void
  onSuccess?(): void
  pushForgotPassword(): void
  username?: string
  children?: ReactNode
}

const DirectLoginFormContainer = ({
  children,
  code = '',
  onValidationStep = () => {},
  onSuccess = () => {},
  username,
}: OwnProps) => {
  const { t } = useTranslation('auth')
  const [localUsername, setUsername] = useState<string | undefined>(username)
  const [forgot, setForgot] = useState<boolean>(false)
  const [emulate, setEmulate] = useState<boolean>(false)

  const loginError = useSelector(selectAuthDirectLoginError)
  const isLoggingIn = useSelector(selectAuthDirectLoginLoading)
  const userId = useSelector(selectAuthUserId)
  const dispatch = useDispatch()

  const {
    formAuthCode,
    formAuthToken,
    error: tokenError,
    expireFormToken,
    refetch: refetchToken,
    isPending: tokenLoading,
  } = useFormToken()

  const handleSubmitPassword = async (values: any) => {
    setUsername(values.username)

    await refetchToken().then(({ data }) => {
      if (!!data) {
        const {
          formAuthToken,
          captchaData: { formAuthCode },
        } = data
        dispatch(
          authDirectLoginWithPassword(
            { ...values, formAuthToken, formAuthCode },
            t('onPasswordLogin')
          )
        )
      }
    })
  }

  const handleSubmitRequestCode = async () => {
    if (formAuthCode && formAuthToken && localUsername) {
      dispatch(
        authDirectRequestVerificationCode({ formAuthCode, formAuthToken, username: localUsername })
      )
    }
  }

  const handleSubmitVerifyCode = async (values: any) => {
    dispatch(authDirectLoginWithVerificationCode(values, t('onCodeLogin')))
  }

  useEffect(() => {
    /**
     * Auto-submit on load if we have all the necessary info
     */
    if (code && formAuthToken && username) {
      handleSubmitVerifyCode({ verifyCode: code, formAuthToken, username })
    }
  }, [])

  useEffect(() => {
    if (userId) {
      onSuccess()
      expireFormToken()
    }
  }, [userId])

  const toggleForgot = (state: boolean) => () => {
    setForgot(state)
    onValidationStep && onValidationStep(state)
  }

  const toggleEmulate = (state: boolean) => () => {
    setEmulate(state)
    onValidationStep && onValidationStep(state)
  }

  if (tokenError) {
    return (
      <Stack spacing={1}>
        <FriendlyError message={t('serverError')} />
        <LoadingButton onClick={() => refetchToken()} loading={tokenLoading}>
          {t('tryAgain')}
        </LoadingButton>
      </Stack>
    )
  }

  if (userId) {
    return <CircularProgress />
  }

  if (forgot) {
    return (
      <Stack spacing={1}>
        <ForgotPassword />
        <Button onClick={toggleForgot(false)}>{t('backToLogin')}</Button>
      </Stack>
    )
  }
  if (emulate) {
    return (
      <Stack spacing={1}>
        <EmulateUserForm />
        <Button onClick={toggleForgot(false)}>{t('backToLogin')}</Button>
      </Stack>
    )
  }

  if (
    (localUsername &&
      loginError &&
      loginError.deniedReason &&
      ['invalid', 'needCode'].includes(loginError.deniedReason)) ||
    (code && formAuthToken)
  ) {
    return (
      <DirectLoginCodeForm
        code={code}
        error={loginError}
        formAuthToken={formAuthToken}
        username={localUsername}
        onSubmit={handleSubmitVerifyCode}
        onResendCode={handleSubmitRequestCode}
      />
    )
  }

  return (
    <DirectLoginPasswordForm
      error={loginError}
      formAuthToken={formAuthToken}
      formAuthCode={formAuthCode}
      loading={isLoggingIn}
      onForgot={() => toggleForgot(true)}
      onEmulate={() => toggleEmulate(true)}
      onSubmit={handleSubmitPassword}
    >
      {children}
    </DirectLoginPasswordForm>
  )
}

export default DirectLoginFormContainer
